import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { Line2 } from "three/addons/lines/webgpu/Line2.js";
import * as THREE from "three/webgpu";
import { DrillEffect } from "~/engine/drill-effects";
import { GameEngine } from "~/engine/instance";
import type { PlayerUpgradeValues, Vector2 } from "~/state/game-types";
import type { Voxel } from "~/terrain/voxel-terrain";
import { disposeMesh } from "~/utils/three";
import { BaseEntity } from "./base";

export class PlayerEntity extends BaseEntity {
  private chassisMesh: THREE.Mesh;
  private renderGroup: THREE.Group;
  private drillEffect = new DrillEffect();

  public boxSize: { width: number; height: number };

  constructor() {
    super();

    const voxelSize = GameEngine.voxelTerrain.voxelSize;

    const EPSILON = 0.001;

    this.boxSize = {
      width: voxelSize * 4 - EPSILON, // tiny epsilon for floating-point precision
      height: voxelSize * 6 - EPSILON, // tiny epsilon for floating-point precision
    };

    // Create chassis (the main player body)
    const texture = new THREE.TextureLoader().load("/assets/player.png");
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.colorSpace = THREE.SRGBColorSpace;
    const chassisMaterial = new THREE.MeshBasicNodeMaterial({
      map: texture,
      // so the mesh is visible on both sides when we flip it
      side: THREE.DoubleSide,
      // make the mesh show up inside of transparent materials properly
      // https://discourse.threejs.org/t/how-to-solve-the-rendering-order-for-random-meshes-to-get-proper-transparence/64164/2
      alphaHash: true,
    });
    // chassisMaterial.lightsNode = lights(GameEngine.renderer.mainLights);
    const chassisGeom = new THREE.PlaneGeometry(voxelSize * 8, voxelSize * 8);
    chassisGeom.translate(0, voxelSize, 0);
    this.chassisMesh = new THREE.Mesh(chassisGeom, chassisMaterial);

    this.renderGroup = new THREE.Group();
    this.renderGroup.add(this.chassisMesh);

    GameEngine.renderer.registerEntity(this.renderGroup);
  }

  public readonly position: Vector2 = { x: 0, y: 0 };

  public velocity: Vector2 = { x: 0, y: 0 };

  public setPosition(newX: number, newY: number): void {
    this.position.x = newX;
    this.position.y = newY;

    this.renderGroup.position.x = newX;
    this.renderGroup.position.y = newY;

    // just for UI and saving
    GameEngine.gameStore.getState().updatePlayerPosition(this.position);
    GameEngine.renderer.updateCameraPosition(this.position);
  }

  public update(_deltaTime: number): void {
    this.updateDigPointerPosition();
  }

  public pointerTarget: Vector2 = { x: 0, y: 0 };
  /** only relevant for laser mining right now */
  public pointerOrigin: Vector2 = { x: 0, y: 0 };
  public pointerAngle: number = 0;

  private updateDigPointerPosition() {
    const playerPos = this.position;

    this.pointerOrigin.x = playerPos.x;
    this.pointerOrigin.y = playerPos.y + this.boxSize.height * -0.05;

    const worldPosition = GameEngine.renderer.screenToWorldPosition(
      GameEngine.inputManager.pointerScreenPosition,
    );

    this.pointerAngle = Math.atan2(
      worldPosition.y - this.pointerOrigin.y,
      worldPosition.x - this.pointerOrigin.x,
    );

    const digPointerOffset =
      GameEngine.gameStore.getState().playerUpgrades.drillOffset;

    const offsetX = Math.cos(this.pointerAngle) * digPointerOffset;
    const offsetY = Math.sin(this.pointerAngle) * digPointerOffset;

    this.pointerTarget.x = playerPos.x + offsetX;
    this.pointerTarget.y = playerPos.y + offsetY;

    // flip the asset left and right depending on if the pointer is facing left or right.
    this.chassisMesh.scale.x = offsetX > 0 ? 1 : -1;
  }

  private laserLine?: Line2;
  private showLaserLine(show: boolean) {
    if (!this.laserLine && show) {
      const drillRadius =
        GameEngine.gameStore.getState().playerUpgrades.drillRadius;
      this.laserLine = new Line2(
        new LineGeometry(),
        new THREE.Line2NodeMaterial({
          linewidth: drillRadius * 2, // this isn't actually in world units, idk
          transparent: true,
          opacity: 0.5,
          color: new THREE.Color(0xff0000),
        }),
      );
      this.laserLine.frustumCulled = false;

      this.laserLine.geometry.setPositions([0, 0, 0, 1, 0, 0]);

      GameEngine.renderer.registerEntity(this.laserLine);
    }
    if (!this.laserLine) return;

    this.laserLine.visible = show;

    if (!show) return;

    const distFromPlayer = this.boxSize.width * 0.85;

    const startX =
      this.pointerOrigin.x + Math.cos(this.pointerAngle) * distFromPlayer;
    const startY =
      this.pointerOrigin.y + Math.sin(this.pointerAngle) * distFromPlayer;

    const endX = this.pointerTarget.x;
    const endY = this.pointerTarget.y;

    const zOffset = 50;

    this.laserLine.geometry.attributes.instanceStart!.setXYZ(
      0,
      startX,
      startY,
      zOffset,
    );
    this.laserLine.geometry.attributes.instanceEnd!.setXYZ(
      0,
      endX,
      endY,
      zOffset,
    );
    this.laserLine.geometry.attributes.instanceStart!.needsUpdate = true;
    this.laserLine.geometry.attributes.instanceEnd!.needsUpdate = true;
  }

  private laserOrigin: Vector2 = { x: 0.0, y: 0.0 };
  public getLaserOrigin(): Vector2 {
    const gameState = GameEngine.gameStore.getState();
    const offset =
      gameState.playerUpgrades.drillRadius + this.boxSize.width * 0.5;
    this.laserOrigin.x =
      this.pointerOrigin.x + Math.cos(this.pointerAngle) * offset;
    this.laserOrigin.y =
      this.pointerOrigin.y + Math.sin(this.pointerAngle) * offset;
    return this.laserOrigin;
  }

  public displayDrillingEffects(
    drillingVoxels: Voxel[] | null,
    miningMode: PlayerUpgradeValues["miningMode"],
  ) {
    this.drillEffect.display(drillingVoxels);

    if (miningMode === "laser") {
      const isDrilling = drillingVoxels != null && drillingVoxels.length > 0;
      this.showLaserLine(isDrilling);
    } else {
      this.showLaserLine(false);
    }
  }

  public dispose() {
    this.drillEffect.dispose();

    disposeMesh(this.renderGroup);
  }
}
